import {
    useEffect,
    type FC
} from "react";
import {
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IBadgeProps from "./type";
import type {
    BadgeIconPosition,
    BadgeWebPropsType
} from "./type";
import stylesheet, {
    getBadgeHitSlop,
    getBadgeColors,
    getBadgeSize,
    useStyles
} from "./stylesheet";
import BadgeIndicator from "./components/indicator";
import useBadgeColors from "./hook";
import BadgeFade from "./internal/fade";
import BadgeLetters from "./internal/letters";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    ArrowUpRight as ArrowUpRightIcon
} from "lucide-react-native";
import {
    warnOnce
} from "../../utils/development";
import {
    openLink
} from "../../helpers/link";
import StateLayer from "../stateLayer";
import Icon from "../icon";
import Text from "../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const Badge: FC<IBadgeProps> = ({
    spreadBehaviour = "baseline",
    "aria-label": ariaLabel,
    iconPosition = "start",
    transition = "fade",
    accessibilityLabel,
    isDisabled = false,
    variant = "tonal",
    customTitleColor,
    type = "neutral",
    customIconColor,
    icon: IconProp,
    size = "small",
    customColor,
    customTheme,
    accessible,
    titleStyle,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    href,
    role,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onFocus,
        onBlur
    });

    const isIconOnly = !!IconProp && !title;

    const isInteractive = !!href || !!onPress;

    const isHoverVisible = isInteractive && !isDisabled && isHovered;

    const currentSize = getBadgeSize({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        typography,
        iconSizes,
        borders,
        spaces,
        size
    });

    const currentColors = getBadgeColors({
        isHovered: isHoverVisible,
        customTitleColor,
        customIconColor,
        customColor,
        isDisabled,
        variant,
        colors,
        type
    });

    const {
        container: containerDynamicStyle,
        title: titleDynamicStyle
    } = useStyles({
        isFocusVisible: isInteractive && isFocusVisible,
        isIconOnly: isIconOnly && !isInteractive,
        isUnderlined: isHoverVisible,
        userStyle: style,
        spreadBehaviour,
        currentColors,
        isInteractive,
        shapeConfigs,
        currentSize,
        radiuses,
        borders,
        colors,
        spaces
    });

    const animatedColors = useBadgeColors({
        currentColors,
        customTheme,
        transition
    });

    useEffect(() => {
        if(!isIconOnly || ariaLabel || accessibilityLabel) {
            return;
        }

        warnOnce({
            message: "Badge: an icon-only badge needs an \"accessibilityLabel\" (or \"aria-label\") so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        isIconOnly,
        ariaLabel
    ]);

    const handlePress = () => {
        onPress?.();

        openLink({
            href
        });
    };

    const renderIcon = (position: BadgeIconPosition) => {
        if(!IconProp || iconPosition !== position) {
            return null;
        }

        return <View
            style={stylesheet.icon}
            aria-hidden={true}
        >
            <IconProp
                customColor={animatedColors.iconColor}
                color={currentColors.iconColorKey}
                size={currentSize.iconSize}
            />
        </View>;
    };

    const renderLinkIcon = () => {
        if(!isInteractive) {
            return null;
        }

        return <Icon
            customColor={animatedColors.iconColor}
            size={currentSize.iconSize}
            customTheme={customTheme}
            style={stylesheet.icon}
            icon={ArrowUpRightIcon}
            isMirrored={true}
        />;
    };

    const renderTitle = () => {
        if(!title) {
            return null;
        }

        if(transition === "morph") {
            return <BadgeLetters
                variant={currentSize.labelVariant}
                color={animatedColors.titleColor}
                titleStyle={isHoverVisible ? [
                    titleStyle,
                    titleDynamicStyle
                ] : titleStyle}
                customTheme={customTheme}
                title={title}
            />;
        }

        if(transition === "fade") {
            return <BadgeFade
                variant={currentSize.labelVariant}
                color={animatedColors.titleColor}
                customTheme={customTheme}
                titleStyle={[
                    titleStyle,
                    stylesheet.title,
                    titleDynamicStyle
                ]}
                title={title}
            />;
        }

        return <Text
            customColor={animatedColors.titleColor}
            variant={currentSize.labelVariant}
            customTheme={customTheme}
            style={[
                titleStyle,
                stylesheet.title,
                titleDynamicStyle
            ]}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    if(!isInteractive) {
        return <Animated.View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle,
                {
                    backgroundColor: animatedColors.containerColor,
                    borderColor: animatedColors.borderColor
                }
            ]}
            role={role ?? (isIconOnly ? "img" : undefined)}
            accessible={isIconOnly ? true : accessible}
            accessibilityLabel={accessibilityLabel}
            aria-disabled={isDisabled}
            aria-label={ariaLabel}
            onFocus={onFocus}
            onBlur={onBlur}
        >
            {renderIcon("start")}
            {renderTitle()}
            {renderIcon("end")}
        </Animated.View>;
    }

    const webProps: BadgeWebPropsType = Platform.OS === "web" && href && !isDisabled ? {
        href
    } : {};

    return <AnimatedPressable
        {...props}
        {...webProps}
        {...interactionHandlers}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle,
            {
                backgroundColor: animatedColors.containerColor,
                borderColor: animatedColors.borderColor
            }
        ]}
        onPress={isDisabled ? undefined : handlePress}
        role={role ?? (href ? "link" : "button")}
        accessibilityLabel={accessibilityLabel}
        hitSlop={getBadgeHitSlop({
            touchTargets,
            currentSize,
            pointerType
        })}
        aria-disabled={isDisabled}
        accessible={accessible}
        aria-label={ariaLabel}
        disabled={isDisabled}
    >
        <StateLayer
            overlayColor={currentColors.overlayColor}
            isPressed={!isDisabled && isPressed}
            borderRadius={radiuses.chip}
            borderWidth={borders.line}
            customTheme={customTheme}
            isSquircle={true}
        />
        {renderIcon("start")}
        {renderTitle()}
        {renderIcon("end")}
        {renderLinkIcon()}
    </AnimatedPressable>;
};
export default Object.assign(Badge, {
    Indicator: BadgeIndicator
});
